/**
 * These are all runtime dependencies. Any breaking changes
 * will need a migration path first!
 */
import type {} from '@sharefiledev/sharefile-appshell'; // force tsc to resolve this package
import { BlockParams } from '@sharefiledev/solution-view-engine';
import { Capabilities } from '../api/requestResponseTypes';
import { WorkflowsStorageZoneDropDownParams } from '../components/extensions/WorkflowsStorageZoneDropDown';
import { MoveAndCopyModalType } from '../components/MoveAndCopy/MoveAndCopyModal';
import { LocaleKeys } from '../locales/allLocaleKeys';
import {
	CustomizableButtonProps,
	CustomizationData,
	DeletedResourceRetrievalType,
	LinkToContainersParams,
	Locale,
	RelatedResource,
	ResourceFilter,
	SubResourceFilter,
	TagFilters,
} from '../types';
import { SideLoadByAssociation } from './../types';

const prefix = 'urn';
const namespace = 'sfextslot';
const modalNamespace = 'sfmodal';
const owner = 'storage-plugin-pilet';
/**
 * @deprecated
 * Seems to be a misunderstanding that Blocks can be used as extension slots
 * interchangeably, so there is now cross-pilet tech debt related to function
 * parameters.
 */
const blockNamespace = `${prefix}:sfblock:storage` as const;
const drawerNamespace = 'sfdrawer';
export const storageExtensionSlots = {
	FilesTable: `${prefix}:${namespace}:${owner}:file-table` as const,
	/**
	 * @deprecated
	 * This is a View Engine block intended to be used by View Engine.
	 * Use as an extension slot is not encouraged, since behavior
	 * cannot be guaranteed independent of View configuration.
	 */
	viewListBlock: `${blockNamespace}:view:list` as const,
	Download: `${prefix}:${namespace}:${owner}:download` as const,
	Count: `${prefix}:${namespace}:${owner}:count` as const,
	ProjectTab: `${prefix}:${namespace}:${owner}:project:tabs:storage` as const,
	ProjectTile: `${prefix}:${namespace}:${owner}:project:home:tiles:storage` as const,
	RecentlyDeletedTab:
		`${prefix}:${namespace}:${owner}:project:recently-deleted:storage-tab` as const,
	/**
	 * @deprecated
	 * This component is not used in View Engine and does not meet Block restrictions.
	 * Use `storageExtensionSlots.FileViewerExtension` instead.
	 */
	FileViewerBlock: `${blockNamespace}:file-viewer` as const,
	FileViewerExtension: `${prefix}:${namespace}:${owner}:file-viewer` as const,
	EditOnline: `${prefix}:${namespace}:${owner}:edit:online` as const,
	EditDesktop: `${prefix}:${namespace}:${owner}:edit:desktop` as const,
	ShareModal: `${prefix}:${namespace}:${owner}:share` as const,
	LinkToContainers: `${prefix}:${namespace}:${owner}:linkToContainer` as const,
	FileViewerDrawer: `${prefix}:${drawerNamespace}:${owner}:file-viewer-drawer` as const,
	/**
	 * @deprecated
	 * This component is not used in View Engine and does not meet Block restrictions.
	 * Use `storageExtensionSlots.StorageZoneDropDown` instead.
	 */
	StorageZoneDropDownBlock: `${blockNamespace}:storage-zone-dropdown` as const,
	StorageZoneDropDown:
		`${prefix}:${drawerNamespace}:${owner}:storage-zone-dropdown` as const,
	/**
	 * @deprecated
	 * This component is not used in View Engine and does not meet Block restrictions.
	 * Use `storageExtensionSlots.WorkflowsStorageZoneDropDown` instead.
	 */
	WorkflowsStorageZoneDropDownBlock:
		`${blockNamespace}:domino:storage-zone-dropdown` as const,
	WorkflowsStorageZoneDropDown:
		`${prefix}:${drawerNamespace}:${owner}:domino:storage-zone-dropdown` as const,
	MoveCopy: `${prefix}:${namespace}:${owner}:move-copy` as const,
	Note: `${prefix}:${namespace}:${owner}:note` as const,
};

export const storagePiletModelKeys = {
	ImportFromStorageProvider:
		`${prefix}:${modalNamespace}:${owner}:import-from-storage-provider` as const,
};

export type StorageFileResourceFetchOptions = {
	resourceState?: DeletedResourceRetrievalType;
	tags?: string[];
	tagFilters?: TagFilters;
	subResourceFilter?: SubResourceFilter;
};

export type StorageFileTableBase = StorageFileResourceFetchOptions & {
	containerRID: string;
};

export type StorageFileTableParams = StorageFileTableBase & {
	customizationData?: CustomizationData;
};

type DownloadLocaleKey = Extract<LocaleKeys, 'download' | 'download_failed'>;

export type DownloadParams = CustomizableButtonProps & {
	containerRID: string;
	downloadItemRIDs: string[];
	hideText?: boolean;
	disable?: boolean;
	labels?: Locale<DownloadLocaleKey>[];
	isContainerDownload?: boolean;
	hideIcon?: boolean;
	sideLoadByAssociation?: SideLoadByAssociation;
	analyticPrefix?: string;
};

export type EditButtonParams = CustomizableButtonProps & {
	containerRID: string;
	fileRID: string;
	hideText?: boolean;
	disable?: boolean;
	hideIcon?: boolean;
	analyticPrefix?: string;
	filter?: ResourceFilter;
	fileViewerConfig?: Record<string, any>;
};

export type ShareModalParams = {
	containerRID: string;
	fileRID?: string;
	versionAgnosticId?: string;
	relatedResourcesPermissions?: RelatedResource[];
	analyticPrefix?: string;
};

export type CountParams = StorageFileTableBase & {
	countClassName?: string;
	loaderClassName?: string;
};

/**
 * @deprecated
 */
export type StorageBlockParams = BlockParams & StorageFileTableParams;

export type FileViewerParams = {
	containerRID: string;
	fileRID: string;
	fileViewerConfig?: any;
};

export type LinkToContainersLocalKey = Extract<LocaleKeys, 'import.link_to_container'>;
export type LinkToContainersBtnParams = CustomizableButtonProps & {
	hideText?: boolean;
	disable?: boolean;
	hideIcon?: boolean;
	analyticPrefix?: string;
	ridsToLink: LinkToContainersParams[];
	ridsToUnlink: string[];
	containerRID: string;
	labels?: Locale<LinkToContainersLocalKey>[];
};

export type FileViewerDrawerOptions = {
	containerRID?: string;
	fileRID: string;
	fileViewerConfig?: any;
	capabilities?: Capabilities;
};

export type StorageZoneDropDownLocalKey = Extract<
	LocaleKeys,
	| 'storage_zone_dropdown.label_description'
	| 'storage_zone_dropdown.label_description_readonly'
	| 'storage_zone_dropdown.tooltip_message'
>;
export type StorageZoneDropDownParams = {
	showLabel?: boolean;
	isReadOnly: boolean;
	containerRID: string;
	onChangeSelectedZone?: (storageZoneRID: string) => void;
	analyticPrefix?: string;
	labels?: Locale<StorageZoneDropDownLocalKey>[];
	hideDropdownHeader?: boolean;
};

export type MoveCopyBtnLocalKey = Extract<
	LocaleKeys,
	'move_copy.action.copy' | 'move_copy.action.move'
>;
export type MoveCopyBtnParams = CustomizableButtonProps & {
	containerRID: string;
	recordsToBeMovedOrCopied: string[];
	action: MoveAndCopyModalType;
	hideText?: boolean;
	disable?: boolean;
	hideIcon?: boolean;
	analyticPrefix?: string;
	labels?: Locale<MoveCopyBtnLocalKey>[];
};

export type NoteBtnLocalKey = Extract<
	LocaleKeys,
	'notes.add_note' | 'notes.edit_note' | 'notes.note_header'
>;

export type NoteBtnParams = CustomizableButtonProps & {
	containerRID: string;
	fileRID: string;
	hideText?: boolean;
	disable?: boolean;
	hideIcon?: boolean;
	analyticPrefix?: string;
	hideNote?: boolean;
	labels?: Locale<NoteBtnLocalKey>[];
	hideButton?: boolean;
};

declare module '@sharefiledev/sharefile-appshell' {
	interface PiralCustomExtensionSlotMap {
		[storageExtensionSlots.FilesTable]: StorageFileTableParams;
		[storageExtensionSlots.Download]: DownloadParams;
		[storageExtensionSlots.EditDesktop]: EditButtonParams;
		[storageExtensionSlots.EditOnline]: EditButtonParams;
		[storageExtensionSlots.ShareModal]: ShareModalParams;
		[storageExtensionSlots.Count]: CountParams;
		[storageExtensionSlots.FileViewerExtension]: FileViewerParams;
		[storageExtensionSlots.LinkToContainers]: LinkToContainersBtnParams;
		[storageExtensionSlots.StorageZoneDropDownBlock]: StorageZoneDropDownParams;
		[storageExtensionSlots.StorageZoneDropDown]: StorageZoneDropDownParams;
		[storageExtensionSlots.WorkflowsStorageZoneDropDownBlock]: WorkflowsStorageZoneDropDownParams;
		[storageExtensionSlots.WorkflowsStorageZoneDropDown]: WorkflowsStorageZoneDropDownParams;
		[storageExtensionSlots.WorkflowsStorageZoneDropDownBlock]: WorkflowsStorageZoneDropDownParams;
		[storageExtensionSlots.WorkflowsStorageZoneDropDown]: WorkflowsStorageZoneDropDownParams;
		[storageExtensionSlots.MoveCopy]: MoveCopyBtnParams;
		[storageExtensionSlots.Note]: NoteBtnParams;
	}
	interface PiralCustomOverlayOptionsMap {
		[storageExtensionSlots.FileViewerDrawer]: FileViewerDrawerOptions;
	}
}
